import constants from "@/config/constants";
import useAuth from "@/hooks/useAuth";
import type { TyAdmin } from "@/hooks/useAuth/types";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyApiResponse } from "@/types";
import OtpInput from "@/UI/inputs/OtpInput";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./styles.css";

const RESEND_SECONDS = constants.otpTtlMinutes * 60;

type TyLocationState = { operationId: string; phone: string } | undefined;

/** Always an existing admin — there is no "new account" arm the way the clients app has one. */
type TyVerifyOtpResponse = TyApiResponse<{ admin: TyAdmin; token: string }>;

type TyLoginResponse = TyApiResponse<{ operationId: string }>;

function formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function VerifyOtpScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as TyLocationState;
    const setAuth = useAuth((store) => store.setAuth);

    const [code, setCode] = useState("");
    const [operationId, setOperationId] = useState(state?.operationId ?? "");
    const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);

    const { data, loading, error, invoke } = useFetch<TyVerifyOtpResponse>("auth/verify-otp", { method: "PUT" }, false);
    const {
        data: resendData,
        loading: resendLoading,
        error: resendError,
        invoke: resendInvoke,
    } = useFetch<TyLoginResponse>("auth/login", { method: "POST" }, false);

    useEffect(() => {
        if (!state?.operationId || !state?.phone) navigate("/login", { replace: true });
    }, [state, navigate]);

    useEffect(() => {
        if (secondsLeft <= 0) return;
        const timer = setInterval(() => setSecondsLeft((prev) => prev - 1), 1000);
        return () => clearInterval(timer);
    }, [secondsLeft]);

    useEffect(() => {
        if (!data?.success) return;
        setAuth(data.data.token, data.data.admin);
        navigate("/");
    }, [data]);

    useEffect(() => {
        if (!resendData?.success) return;
        setOperationId(resendData.data.operationId);
        setSecondsLeft(RESEND_SECONDS);
        setCode("");
    }, [resendData]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (code.length !== constants.otpLength) return;
        invoke({ data: { code, operationId } });
    };

    const handleResend = () => {
        if (secondsLeft > 0 || !state?.phone) return;
        resendInvoke({ data: { phone: state.phone } });
    };

    return (
        <div className="verify-otp">
            <form className="verify-otp-card" onSubmit={handleSubmit}>
                <div className="verify-otp-header">
                    <div className="verify-otp-icon-wrap">
                        <div className="verify-otp-icon-inner">
                            <img src="/icons/otp-shield-icon.svg" className="verify-otp-icon" alt="" />
                        </div>
                    </div>
                    <div className="verify-otp-texts">
                        <p className="verify-otp-title">{t.verifyOtp.title}</p>
                        <p className="verify-otp-subtitle">{t.verifyOtp.subtitle}</p>
                    </div>
                </div>

                <div className="verify-otp-body">
                    <div className="verify-otp-timer">
                        <p className="verify-otp-timer-text">{formatTime(secondsLeft)}</p>
                        <img src="/icons/timer-icon.svg" className="verify-otp-timer-icon" alt="" />
                    </div>

                    <OtpInput value={code} onChange={setCode} length={constants.otpLength} disabled={loading} />

                    <div className="verify-otp-resend">
                        <button type="button" className="verify-otp-resend-link" onClick={handleResend} disabled={secondsLeft > 0 || resendLoading}>
                            {t.verifyOtp.resendLink}
                        </button>
                        <p className="verify-otp-resend-hint">{t.verifyOtp.resendHint}</p>
                    </div>
                </div>

                {(error ?? resendError) && <p className="verify-otp-error">{error ?? resendError}</p>}

                <button type="submit" className="verify-otp-submit" disabled={code.length !== constants.otpLength || loading}>
                    {t.verifyOtp.submitButton}
                </button>

                <button type="button" className="verify-otp-back" onClick={() => navigate("/login")}>
                    {t.verifyOtp.reEnterPhone}
                </button>
            </form>
        </div>
    );
}
